import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const floatingItems = [
  { icon: "ri-plant-line", size: "text-3xl", x: "10%", y: "20%", delay: "0s", duration: "6s" },
  { icon: "ri-fire-line", size: "text-2xl", x: "85%", y: "15%", delay: "1s", duration: "7s" },
  { icon: "ri-gift-line", size: "text-4xl", x: "75%", y: "70%", delay: "0.5s", duration: "8s" },
  { icon: "ri-heart-line", size: "text-2xl", x: "15%", y: "75%", delay: "2s", duration: "5s" },
  { icon: "ri-star-line", size: "text-xl", x: "50%", y: "10%", delay: "1.5s", duration: "9s" },
  { icon: "ri-leaf-line", size: "text-3xl", x: "90%", y: "45%", delay: "0.8s", duration: "7s" },
  { icon: "ri-flower-line", size: "text-2xl", x: "5%", y: "50%", delay: "2.5s", duration: "6s" },
  { icon: "ri-sparkling-line", size: "text-xl", x: "60%", y: "85%", delay: "1.2s", duration: "8s" },
];

export default function HeroSection() {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const words1 = t("hero_headline").split(" ");
  const words2 = t("hero_headline2").split(" ");

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Parallax */}
      <div
        className="absolute inset-0 will-change-transform"
        style={{ transform: `translateY(${scrollY * 0.4}px)` }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=luxurious%20gift%20shop%20interior%20with%20beautiful%20flowers%20roses%20candles%20gift%20boxes%20on%20elegant%20cream%20marble%20table%2C%20warm%20golden%20ambient%20light%2C%20premium%20boutique%20aesthetic%2C%20romantic%20and%20elegant%20atmosphere%2C%20soft%20pink%20and%20gold%20tones%2C%20high%20end%20photography&width=1920&height=1080&seq=hero1&orientation=landscape"
          alt="Creative Hub Premium Gift Shop"
          className="w-full h-full object-cover object-top scale-110"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-rose-900/75 via-amber-900/50 to-rose-800/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
      </div>

      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-96 h-96 rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, #d4a96a, transparent)",
            top: "10%",
            left: "5%",
            animation: "float1 8s ease-in-out infinite",
          }}
        />
        <div
          className="absolute w-80 h-80 rounded-full opacity-15"
          style={{
            background: "radial-gradient(circle, #fbbf24, transparent)",
            bottom: "15%",
            right: "10%",
            animation: "float2 10s ease-in-out infinite",
          }}
        />
      </div>

      {/* Floating Icons */}
      {floatingItems.map((item, i) => (
        <div
          key={i}
          className="absolute pointer-events-none text-white/20"
          style={{
            left: item.x,
            top: item.y,
            fontSize: "inherit",
            animation: `floatIcon ${item.duration} ease-in-out infinite`,
            animationDelay: item.delay,
          }}
        >
          <i className={`${item.icon} ${item.size}`}></i>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-6 text-center">
        {/* Badge */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 text-white text-xs font-semibold uppercase tracking-widest mb-8 transition-all duration-700 ${
            loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <i className="ri-map-pin-line text-amber-300"></i>
          1401 Melrose Place, Akwa, Douala
          <span className="w-1.5 h-1.5 rounded-full bg-rose-300 animate-pulse"></span>
        </div>

        {/* Headline */}
        <h1
          className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight mb-4"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          <div className="flex flex-wrap justify-center gap-x-4">
            {words1.map((word, i) => (
              <span
                key={i}
                className="inline-block transition-all duration-700"
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? "translateY(0)" : "translateY(30px)",
                  transitionDelay: `${0.3 + i * 0.1}s`,
                }}
              >
                {word}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-x-4 mt-2">
            {words2.map((word, i) => (
              <span
                key={i}
                className="inline-block text-amber-300 italic transition-all duration-700"
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? "translateY(0)" : "translateY(30px)",
                  transitionDelay: `${0.5 + i * 0.1}s`,
                }}
              >
                {word}
              </span>
            ))}
          </div>
        </h1>

        {/* Subtitle */}
        <p
          className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10 transition-all duration-700"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "0.8s",
          }}
        >
          {t("hero_subtitle")}
        </p>

        {/* Buttons */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "1s",
          }}
        >
          <Link
            to="/shop"
            className="group flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-600 to-rose-600 text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-amber-500/40 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shopping-bag-line text-lg"></i>
            {t("hero_btn_shop")}
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200"></i>
          </Link>
          <Link
            to="/custom-gift-box"
            className="flex items-center gap-2 px-8 py-4 bg-white/15 backdrop-blur-sm border border-white/40 text-white font-semibold rounded-full hover:bg-white/25 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-gift-2-line text-lg"></i>
            {t("hero_btn_custom")}
          </Link>
          <Link
            to="/contact"
            className="flex items-center gap-2 px-8 py-4 bg-transparent border border-white/40 text-white/90 font-semibold rounded-full hover:border-white hover:text-white hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-message-line text-lg"></i>
            {t("hero_btn_contact")}
          </Link>
        </div>

        {/* Scroll indicator */}
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-700"
          style={{
            opacity: loaded ? 0.7 : 0,
            transitionDelay: "1.5s",
          }}
        >
          <span className="text-white/60 text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-5 h-8 border-2 border-white/40 rounded-full flex items-start justify-center p-1">
            <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce" />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatIcon {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(5deg); }
          66% { transform: translateY(10px) rotate(-3deg); }
        }
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -20px) scale(1.1); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-20px, 30px) scale(0.9); }
        }
      `}</style>
    </section>
  );
}